import React from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, HPMSOnRenderAction, setVisibility } from '@philips-emr/hpms-components';
import { ActionAPI, ContextualBarAPI, DeckAPI, DeckValue, Link, PanelAPI, Stamp, StampIcon, TabWithContentAPI } from '@philips-emr/tws-components';
import { Color, ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnNavigate, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';

import { DetalhesAutorizacaoAliases, FragmentNames } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { DownloadService, ReportService } from '../../services';
import BaseController from '../base/base-controller';

enum Status {
  A = 'A',
  C = 'C',
  D = 'D',
  I = 'I',
  K = 'K',
  L = 'L',
  M = 'M',
  N = 'N',
  P = 'P',
  S = 'S',
  U = 'U',
}

@Controller({ fragment: FragmentNames.DETALHES_AUTORIZACAO })
export default class DetalhesAutorizacaoController extends BaseController {

  @Inject(DetalhesAutorizacaoAliases.PANEL_AUTORIZACAO)
  private panelAutorizacao: PanelAPI;
  @Inject(DetalhesAutorizacaoAliases.CONTEXTUAL_BAR_0)
  private contextBarDadosAutorizacao: ContextualBarAPI;
  @Inject(DetalhesAutorizacaoAliases.TAB_DADOS_AUTORIZACAO)
  private tabDadosAutorizacao: TabWithContentAPI;
  @Inject(DetalhesAutorizacaoAliases.TAB_ANEXOS_TISS)
  private tabAnexosTiss: TabWithContentAPI;
  @Inject(DetalhesAutorizacaoAliases.ACTION_IMPRIMIR)
  private buttonImprimir: ActionAPI;
  @Inject(DetalhesAutorizacaoAliases.PANEL_GLOSAS_OCORRENCIAS)
  private panelGlosasOcorrencias: PanelAPI;
  @Inject(DetalhesAutorizacaoAliases.PANEL_ANEXOS)
  private panelAnexos: PanelAPI;

  @OnMount(DetalhesAutorizacaoAliases.LAYOUT_DETALHES_AUTORIZACAO)
  onMount(): void {
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqGuia, origem: 'autorizacao', nrSeq: nrSeqGuia });
  }

  @OnMount(DetalhesAutorizacaoAliases.PANEL_LOADER)
  onLoaderMount({ target }: MountEvent): void {
    const api = target as PanelAPI;
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();
    const { nrSeqBeneficiario } = this.context.getBulkData();

    HttpService.get<boolean>(Endpoints.GET.CHECK_GUIA_TISS, { nrSeqGuia, nrSeqBeneficiario })
      .then(({ body }) => catchRenderException(() => {
        api.setVisible(false);
        this.panelAutorizacao.setVisible(true);
        if (body) {
          this.tabAnexosTiss.setVisible(!!body);
          this.tabDadosAutorizacao.setVisible(!!body);
          this.contextBarDadosAutorizacao.setTitle('');
          this.context.set({ hasTiss: true });
        } else {
          this.tabAnexosTiss.setVisible(!!body);
          this.tabDadosAutorizacao.setVisible(!!body);
        }
      }))
      .catch(() => catchRenderException(() => {
        api.setVisible(false);
        this.panelAutorizacao.setVisible(true);
        this.tabAnexosTiss.setVisible(false);
      }));
  }

  @OnNavigate(DetalhesAutorizacaoAliases.TAB_CONTROL_AUTORIZACAO)
  onTabChange(): void {
    const { hasTiss } = this.context.getBulkData();
    if (hasTiss) {
      this.contextBarDadosAutorizacao.setTitle('');
    }
  }

  @OnAfterFetch(DetalhesAutorizacaoAliases.FETCH_AUTORIZACAO)
  onAutorizacaoAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { nrSeqBeneficiario, nrSeqGuia } = this.context.getBulkData();
      const { ieTipoGuia, ieStatus } = data as { ieTipoGuia: string, ieStatus: string };

      if (ieStatus === '1') {
        void ReportService.print({
          cdGrupo: '11',
          nrSeqBeneficiario: nrSeqBeneficiario as number,
          endpoint: Endpoints.GET.RELATORIO_AUTORIZACAO,
          parametrosEndpoint: { nrSeqGuia, ieTipoGuia },
          parametrosRelatorio: { NR_SEQUENCIA_AUTOR: nrSeqGuia },
        })
          .then(({ event }) => {
            this.buttonImprimir.setEnabled(true);
            this.buttonImprimir.onClick.listen(event);
          });
      }
    }
  }

  @OnClick(DetalhesAutorizacaoAliases.ACTION_IMPRIMIR)
  onPrint(): void {
    const { nrSeqBeneficiario, nrSeqGuia } = this.context.getBulkData();
    void ReportService.print({
      cdGrupo: '10',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    })
      .then(({ record }) => {
        void ReportService.open({
          cdGrupo: '11',
          nrSeqBeneficiario: nrSeqBeneficiario as number,
          parametrosRelatorio: { NR_SEQUENCIA_AUTOR: nrSeqGuia },
          record,
        });
        return true;
      })
      .catch(() => false);
  }

  @OnAfterFetch(DetalhesAutorizacaoAliases.FETCH_ANEXOS)
  onAnexosAfterFetch = (event: FetchEvent): void => setVisibility(event, this.panelAnexos);

  @OnAfterFetch(DetalhesAutorizacaoAliases.FETCH_GLOSAS_OCORRENCIAS)
  onGlosasOcorrenciasAfterFetch = (event: FetchEvent): void => setVisibility(event, this.panelGlosasOcorrencias);

  @OnSelectItem(DetalhesAutorizacaoAliases.DECK_ANEXOS)
  onSelectItem({ item, target }: SelectItemEvent<DeckValue>): void {
    const api = target as DeckAPI;
    api.unselectAll();

    const { nrSeqBeneficiario, nrSeqGuia } = this.context.getBulkData();
    const { nmArquivo, nrSequencia } = item;

    const parameters = {
      nrSeqAnexo: nrSequencia,
      nrSeqBeneficiario,
      nrSeqGuia,
    };

    DownloadService.of({
      endpoint: Endpoints.GET.DOWNLOAD_AUTORIZACAO,
      name: nmArquivo as string,
      parameters,
    });
  }

  @OnDynamicContentRender(DetalhesAutorizacaoAliases.CUSTOM_CARD_ITEM)
  onDynamicContentRender({ data, render }: RenderEvent): void {
    const { ieStatus } = data as { ieStatus: Status };

    const props = {
      [Status.A]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING },
      [Status.C]: { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK },
      [Status.D]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE },
      [Status.I]: { className: 'c-roxo-stamp__stamp--color', icon: StampIcon.CANCELED_OUTLINE },
      [Status.K]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED },
      [Status.L]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.M]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED },
      [Status.N]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED },
      [Status.P]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.S]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.U]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE },
    };

    render(() => <Stamp {...props[ieStatus]} label={`@beneficiary.situacao-autorizacao/${ieStatus.toLowerCase()}`} />);
  }

  @HPMSOnRenderAction(DetalhesAutorizacaoAliases.CUSTOM_CARD_ITEM)
  onRenderAction({ render, data }: RenderEvent): void {
    const { ieTipoAnexo, nrSeqItem } = data as { ieTipoAnexo: string, nrSeqItem: number };

    if (['OP', 'QU'].includes(ieTipoAnexo)) {
      render(() => (<Link {...{
        icon: '/static/assets/DLS_Details.svg',
        onClick: () => this.onModalItemOpen(nrSeqItem, ieTipoAnexo),
        value: '',
      }} />));
    }
  }

  private onModalItemOpen(nrSeqMat: number, ieTipoAnexo: string): void {
    const { nrSeqBeneficiario, nrSeqGuia } = this.context.getBulkData();

    ModalService.openDefault({
      closeable: true,
      contextData: {
        nrSeqMat,
        ieTipoAnexo,
        origem: 'autorizacao',
        nrSeqOrigem: nrSeqGuia,
        nrSeqBeneficiario,
      },
      fragment: FragmentNames.MODAL_ITEM_TISS,
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'beneficiary.detalhes-item',
    });
  }
}
